"use client"

import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { useTranslations } from 'next-intl'

const Content = () => {
    const t = useTranslations('Chatbot');

    // Refs for scroll animations
    const mainRef = useRef(null)
    const featuresRef = useRef(null)

    // Check if elements are in view
    const mainInView = useInView(mainRef, { once: true, margin: "-100px" })
    const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" })

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.5,
                delayChildren: 0.3
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8
            }
        }
    }

    const cardVariants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    // Individual card variants with specific delays
    const card1Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card2Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 0.7
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }

    const card3Variants = {
        hidden: { opacity: 0, y: 30, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                delay: 1.2
            }
        },
        hover: {
            scale: 1.02,
            y: -5,
            transition: {
                duration: 0.3
            }
        }
    }


    return (
        <div className="">
            <motion.div
                ref={mainRef}
                initial="hidden"
                animate={mainInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.h3
                    className="text-3xl font-normal text-red-600 dark:text-red-400 mb-8 italic -mt-8"
                    variants={itemVariants}
                >
                    {t('main_title')}
                </motion.h3>
            </motion.div>

            {/* Mapa de votantes section */}


            {/* Comunicación 24/7 section */}
            <motion.div
                className="mb-8"
                initial="hidden"
                animate={mainInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={itemVariants}
                >
                    <h4 className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('communication_24_7.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('communication_24_7.description')}
                    </p>
                </motion.div>
            </motion.div>

            <motion.div
                ref={featuresRef}
                className="grid md:grid-cols-2 gap-8"
                initial="hidden"
                animate={featuresInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                {/* Conseguir nuevos votantes */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card1Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.new_voters.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.new_voters.description')}
                    </p>
                </motion.div>

                {/* Fidelizar la base de votantes */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card2Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.voter_loyalty.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.voter_loyalty.description')}
                    </p>
                </motion.div>

                {/* Volcar votantes indecisos */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={card3Variants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.undecided_voters.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.undecided_voters.description')}
                    </p>
                </motion.div>

                {/* Mantener una comunicación permanente es crear y manejar la agenda */}
                <motion.div
                    className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-lg"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <h4 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-4">{t('features.permanent_communication.title')}</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {t('features.permanent_communication.description')}
                    </p>
                </motion.div>
            </motion.div>

        </div>
    )
}

export default Content
